import type { AssignedInquiry } from '@ghp/domain'

import type { InspectionDraft, OutboxEntry, OutboxState } from '../../../offline/data-db'
import { outboxStateOf } from '../../../offline/outbox'

/**
 * Les saisies **dont la demande a quitté la tournée** (#147).
 *
 * Le bureau réaffecte une demande à une autre équipe, et la tournée qui redescend ne la
 * porte plus : « Mes contrôles » part des demandes, la ligne s'en va — mais le brouillon,
 * lui, reste. Un relevé non transmis n'existe **nulle part ailleurs** que sur cette
 * tablette, et sans cette lecture il ne se rouvre plus que par son URL.
 *
 * D'où une lecture de `drafts` **indépendante de la liste des demandes** : c'est le seul
 * angle sous lequel une saisie sortie de la tournée reste visible.
 */
export interface OrphanDraft {
  draft: InspectionDraft
  /**
   * La demande telle que la tablette l'a connue — c'est elle qui porte la référence et
   * l'adresse. La descente ne retire pas une demande qui porte une saisie, elle est donc là ;
   * `null` couvre la tablette dont
   * la base a été refaite, où il ne reste que l'identifiant du contrôle.
   */
  inquiry: AssignedInquiry | null
  /** `null` tant que rien n'a été mis en file pour ce contrôle. */
  sync: OutboxState | null
  attachments: number
}

/**
 * Les brouillons **non transmis** que la dernière tournée complète ne portait plus.
 *
 * ⚠️ Le juge est `outOfTourAt`, posé par la descente (`offline/pull.ts`), **jamais l'absence
 * de la demande dans le cache** : ce cache ne perd jamais rien, et s'y fier ferait une
 * section qui ne s'affiche jamais. L'appartenance à l'équipe ne se lit pas non plus ici —
 * `AssignedInquiry` ne porte pas de `teamId`, et le serveur ne sert à la tablette que la
 * tournée de l'équipe connectée : « la tournée ne la porte plus » est exactement ce que la
 * tablette sait.
 *
 * ⚠️ **Un relevé déjà rangé par le serveur sort de la liste.** Transmettre fait passer la
 * demande en `submitted`, que l'API ne sert plus à l'équipe (`FIELD_VISIBLE_STATUSES`) : une
 * saisie transmise quitte donc la tournée par la porte normale. La signaler ici noierait le
 * seul cas où il reste quelque chose à sauver — un relevé qui n'existe que sur cette
 * tablette — sous toute la journée de travail de l'équipe.
 *
 * Le plus récemment touché d'abord : c'est celui que l'agent vient chercher.
 */
export function orphanDraftsOf(
  drafts: readonly InspectionDraft[],
  inquiries: readonly AssignedInquiry[],
  outbox: OutboxEntry[],
  attachments: ReadonlyMap<string, number>,
): OrphanDraft[] {
  const known = new Map(inquiries.map((inquiry) => [inquiry.id, inquiry]))

  return drafts
    .filter((draft) => draft.outOfTourAt != null)
    .map((draft) => ({
      draft,
      inquiry: known.get(draft.inquiryId) ?? null,
      sync: outboxStateOf(outbox, draft.inquiryId),
      attachments: attachments.get(draft.inquiryId) ?? 0,
    }))
    .filter((row) => row.sync !== 'sent')
    .toSorted((left, right) => right.draft.updatedAt.localeCompare(left.draft.updatedAt))
}
